import type { ReactNode } from "react";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { Spinner } from "@app/components/base/spinner/spinner";

interface LoadingCardProps {
    /** Bold primary label, also announced as the accessible name via `role="status"`. */
    label: string;
    /** Optional secondary description rendered under the label. */
    description?: ReactNode;
    /** Additional classes applied to the container. */
    className?: string;
    /**
     * Whether to wrap the content in a bordered card.
     * @default true
     */
    bordered?: boolean;
}

/**
 * Reusable loading card: a centered spinner with a bold label and an optional
 * secondary description, in an accessible `role="status"` container.
 *
 * Shared building block for section/first-load loaders so their look stays uniform
 * across the app. {@link DataTableLoading} delegates to this component.
 */
export function LoadingCard({ label, description, className, bordered = true }: LoadingCardProps) {
    return (
        <div
            role="status"
            aria-label={label}
            className={cx("flex flex-col items-center justify-center gap-1.5", bordered && "rounded-xl border border-gray-200 bg-white", className)}
        >
            <Spinner className="mb-1 h-5 w-5 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">{label}</span>
            {description && <span className="text-xs text-gray-400">{description}</span>}
        </div>
    );
}
